/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { ConsentCategory, ConsentPreferences } from '@salesforce/storefront-next-runtime/events';
import { TrackingConsent } from '@/types/tracking-consent';
import { createLogger } from '@/lib/logger';
import type { OneTrustConfig, AppConfig } from '@/types/config';
import { readActiveGroups } from '@/components/onetrust-bridge/mapping';

const logger = createLogger();

/**
 * Input required to derive `ConsentPreferences` from OneTrust's per-category
 * decision (INT-60 PR 4). Callers construct this on the client after the OT
 * script has surfaced `window.OnetrustActiveGroups`. Passing the resolved Set
 * (rather than reading `window` inside `buildConsentPreferences`) keeps the
 * helper pure and SSR-safe.
 */
export type OneTrustBridgingInput = {
    /** `appConfig.onetrust` snapshot — the presence and category map. */
    config: OneTrustConfig;
    /** OT active-groups Set. Read on the client via `readActiveGroups()`. */
    activeGroups: Set<string>;
};

/**
 * Fixed translation between OneTrust category keys (as stored on
 * `OneTrustConfig.categoryMap`) and the SFN `ConsentCategory` strings the
 * engagement adapters gate on. Centralized here so a new adapter can be
 * introduced with a single-source mapping table.
 *
 * OneTrust category IDs are configurable per tenant (though C0001–C0004 is
 * the canonical Cookies template), but the *semantic keys*
 * (`strictlyNecessary` / `analytics` / `functional` / `advertising`) are
 * fixed by the CMP. We only vary the OT ID via `OneTrustConfig.categoryMap`
 * — the mapping to SFN strings is static.
 */
/**
 * Assemble the {@link OneTrustBridgingInput} from the app config and the
 * client's current `window.OnetrustActiveGroups`. Returns `undefined` when
 * OneTrust is disabled (so `buildConsentPreferences()` falls through to its
 * binary branch) or when called from the server (no `window`).
 *
 * Callers (`useAnalytics`, `PageViewTracker`) should invoke this once per
 * render and pass the result straight through — it deliberately does not
 * memoize because the underlying `OnetrustActiveGroups` string mutates
 * without a stable reference OT can emit.
 */
export function resolveOneTrustBridging(appConfig: AppConfig | undefined): OneTrustBridgingInput | undefined {
    const config = appConfig?.onetrust;
    if (!config || config.enabled !== true) {
        return undefined;
    }
    return { config, activeGroups: readActiveGroups() };
}

const ONETRUST_TO_SFN_CATEGORY = {
    strictlyNecessary: 'necessary',
    analytics: 'analytics',
    functional: 'personalization',
    advertising: 'marketing',
} as const satisfies Record<keyof OneTrustConfig['categoryMap'], ConsentCategory>;

/**
 * Check whether there is sufficient consent to send events.
 *
 * Returns true (allow) when:
 * - The user granted at least one category and the adapter has no consentCategory
 *   configured (adapter doesn't require specific consent)
 * - The adapter's consentCategory is included in the consent preferences
 *
 * Returns false when:
 * - No consentPreferences are provided (undefined means consent state is unknown or
 *   not yet determined — the safe default is to block)
 * - consentPreferences is an empty array (user explicitly declined all tracking)
 * - The adapter's consentCategory is not in the granted preferences
 *
 * @param adapterConsentCategory - The consent category configured on the adapter
 * @param consentPreferences - The consent preferences from the consent system
 * @returns true if there is sufficient consent, false otherwise
 */
export function hasConsent(adapterConsentCategory?: ConsentCategory, consentPreferences?: ConsentPreferences): boolean {
    // Consent state unknown or not yet determined — block
    if (!consentPreferences) {
        return false;
    }
    // User explicitly declined all tracking
    if (consentPreferences.length === 0) {
        return false;
    }
    // Adapter doesn't require a specific category — allow
    if (!adapterConsentCategory) {
        return true;
    }
    return consentPreferences.includes(adapterConsentCategory);
}

/**
 * Build consent preferences from the visitor's consent decision (binary
 * `TrackingConsent` and/or OneTrust per-category state).
 *
 * ## Resolution order (highest priority first)
 *
 * 1. **OneTrust (INT-60 PR 4)** — when `onetrust.config.enabled === true`,
 *    the CMP is the source of truth. If `activeGroups` is non-empty, each
 *    granted OT group ID is mapped to its SFN `ConsentCategory` via
 *    `onetrust.config.categoryMap` and returned. If the set is empty (script
 *    still loading, or the shopper hasn't been prompted yet), returns
 *    `undefined` so the hook layer blocks all tracking. In this branch the
 *    binary `trackingConsent` argument is ignored — the OneTrust bridge
 *    (see `<OneTrustBridge />`) keeps `dw_dnt` in sync as a *secondary*
 *    signal for SFCC server-side consumers.
 *
 * 2. **Consent system off** — when `isTrackingConsentEnabled === false`,
 *    tracking is unrestricted. Returns the configured category list (or
 *    `['necessary']` as a floor).
 *
 * 3. **Binary consent (legacy / pre-OT)** — when the consent system is on
 *    but OneTrust is disabled, the existing `TrackingConsent.Accepted` /
 *    `Declined` / `undefined` branches apply: Accepted returns all
 *    configured categories, Declined returns `[]`, undefined returns
 *    `undefined` (block).
 *
 * @param trackingConsent - The visitor's binary consent status. Ignored when the OneTrust branch fires.
 * @param consentCategories - The configured consent categories (from `config.engagement.analytics.trackingConsent.consentCategories`)
 * @param isTrackingConsentEnabled - Whether the tracking-consent system is active at all
 * @param onetrust - Optional OneTrust bridging input. Callers assemble this on the client (SSR-safe pass-through) via `readActiveGroups()` + `appConfig.onetrust`.
 * @returns `ConsentPreferences` with granted categories, empty array if declined, or `undefined` if not yet determined.
 *          When `undefined` is returned, the hook layer (`useAnalytics`, `PageViewTracker`) blocks all tracking.
 */
export function buildConsentPreferences(
    trackingConsent: TrackingConsent | undefined,
    consentCategories: ConsentCategory[],
    isTrackingConsentEnabled: boolean,
    onetrust?: OneTrustBridgingInput
): ConsentPreferences | undefined {
    if (onetrust?.config.enabled === true) {
        return buildConsentPreferencesFromOneTrust(onetrust);
    }
    // Consent system not active — allow all tracking unrestricted
    if (!isTrackingConsentEnabled) {
        return consentCategories.length > 0 ? [...consentCategories] : ['necessary'];
    }
    if (trackingConsent === TrackingConsent.Accepted) {
        if (consentCategories.length === 0) {
            logger.warn(
                'Tracking consent is enabled but consentCategories is empty. ' +
                    'Analytics events will be blocked. Add categories to ' +
                    'config.engagement.analytics.trackingConsent.consentCategories.'
            );
        }
        return [...consentCategories];
    }
    if (trackingConsent === TrackingConsent.Declined) {
        return [];
    }
    // Consent enabled but not yet determined — block tracking
    return undefined;
}

/**
 * OneTrust-only branch of {@link buildConsentPreferences}. Reads the active
 * group set, maps each granted group to its SFN `ConsentCategory` via the
 * configured `categoryMap`, and returns the deduplicated list.
 *
 * Returns `undefined` when the OT script has not populated `activeGroups`
 * yet (Set is empty). This matches the "consent not yet determined"
 * semantics of the binary branch and is what the hook layer treats as
 * "block all events" — critical so we don't fire pre-consent beacons
 * during hydration on fast connections that race the OT script.
 */
function buildConsentPreferencesFromOneTrust(onetrust: OneTrustBridgingInput): ConsentPreferences | undefined {
    if (onetrust.activeGroups.size === 0) {
        return undefined;
    }
    const { categoryMap } = onetrust.config;
    const preferences: ConsentPreferences = [];
    for (const [key, sfnCategory] of Object.entries(ONETRUST_TO_SFN_CATEGORY) as [
        keyof OneTrustConfig['categoryMap'],
        ConsentCategory,
    ][]) {
        const otGroupId = categoryMap[key];
        if (otGroupId && onetrust.activeGroups.has(otGroupId)) {
            preferences.push(sfnCategory);
        }
    }
    return preferences;
}
